import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SETTINGS_INPUT_CLASS, SETTINGS_PILL_BUTTON_CLASS } from './settings-ui';

interface ChipInputProps {
	values: string[];
	/** Return `false` to reject the value; it then stays in the box to fix. */
	onAdd: (value: string) => boolean | void | Promise<boolean | void>;
	onRemove: (value: string) => void;
	placeholder: string;
	disabled?: boolean;
	/** Shows an "Add" button beside the box; without it, Enter adds. */
	withAddButton?: boolean;
	'aria-label'?: string;
}

// Ingestion motion spec, in CSS: an added chip grows in from the left, a removed one shrinks out, a rejected
// value shakes the box. Off for reduced motion.
const CHIP_CLASS = 'flex h-[26px] origin-left items-center gap-1 rounded-full border border-gray-500 pl-2.5 pr-2';
const CHIP_IN_CLASS = 'animate-[settings-chip-in_260ms_cubic-bezier(0.3,1.35,0.5,1)] motion-reduce:animate-none';
const CHIP_OUT_MS = 140;
const CHIP_OUT_CLASS = 'pointer-events-none animate-[settings-chip-out_140ms_ease-in_forwards] motion-reduce:hidden';

/** A text box that adds chips below it (Figma blocked domains / tags). */
export function ChipInput({ values, onAdd, onRemove, placeholder, disabled, withAddButton, 'aria-label': ariaLabel }: ChipInputProps) {
	const [draft, setDraft] = useState('');
	const [shaking, setShaking] = useState(false);
	// Only chips added here animate in; ones already saved just appear.
	const [added, setAdded] = useState<string | null>(null);

	// Chips on screen: the values, plus any just removed, which shrink out where they were. The removal itself
	// saves at once; only its exit is animated.
	const [shown, setShown] = useState(values);
	const [lastValues, setLastValues] = useState(values);
	if (values !== lastValues) {
		setLastValues(values);
		// The values' own order, with each removed chip kept at the place it had.
		const next = [...values];
		for (const value of shown) if (!values.includes(value)) next.splice(Math.min(shown.indexOf(value), next.length), 0, value);
		setShown(next);
	}
	const leaving = shown.filter((value) => !values.includes(value));
	useEffect(() => {
		if (!leaving.length) return;
		const timer = setTimeout(() => setShown((current) => current.filter((value) => values.includes(value))), CHIP_OUT_MS);
		return () => clearTimeout(timer);
	}, [leaving.length, values]);

	const add = async () => {
		const value = draft.trim();
		if (!value) return;
		// A rejected value (a duplicate, say) gives the box a small shake.
		if ((await onAdd(value)) === false) {
			setShaking(true);
			return;
		}
		setAdded(value);
		setDraft('');
	};

	return (
		<div className='flex w-full flex-col gap-4'>
			<div
				onAnimationEnd={() => setShaking(false)}
				className={cn('flex w-full items-center gap-2', shaking && 'animate-[settings-shake_250ms] motion-reduce:animate-none')}
			>
				<input
					type='text'
					value={draft}
					onChange={(e) => setDraft(e.target.value)}
					onKeyDown={(e) => {
						if (e.key !== 'Enter') return;
						e.preventDefault();
						void add();
					}}
					placeholder={placeholder}
					disabled={disabled}
					aria-label={ariaLabel ?? placeholder}
					className={SETTINGS_INPUT_CLASS}
				/>
				{withAddButton && (
					<button type='button' onClick={() => void add()} disabled={disabled || !draft.trim()} className={SETTINGS_PILL_BUTTON_CLASS}>
						Add
					</button>
				)}
			</div>
			<ul className='flex flex-wrap gap-2 empty:hidden'>
				{shown.map((value) => (
					<li
						key={value}
						aria-hidden={leaving.includes(value) || undefined}
						className={cn(CHIP_CLASS, value === added && CHIP_IN_CLASS, leaving.includes(value) && CHIP_OUT_CLASS)}
					>
						<span className='text-[11px] leading-4 text-text-emphasis'>{value}</span>
						<button
							type='button'
							onClick={() => onRemove(value)}
							disabled={disabled || leaving.includes(value)}
							aria-label={`Remove ${value}`}
							className='flex cursor-pointer items-center justify-center rounded-full text-gray-900 hover:text-ink disabled:cursor-not-allowed disabled:opacity-50'
						>
							<X className='size-3' />
						</button>
					</li>
				))}
			</ul>
		</div>
	);
}
